// Portfolio Administration v1.0 PR 4b §7.8 — collapsed list of closed
// DefaultCases for a loan. Surfaces the resolution history without
// adding noise to the empty-state view. Each row links to the case
// (deep-link via ?case=<id>) for read-only inspection.

import { Badge } from "@/design-system/components/ui";
import { AccordionCard } from "./AccordionCard";
import type { DefaultCaseDto } from "@/api/portfolioAdmin/pr4bTypes";
import { formatCalendarDate, formatDateInTz, useDateFormatter } from "@/utils/datetime";

interface HistoricalDefaultsListProps {
  cases: DefaultCaseDto[];
  onSelect?: (caseId: string) => void;
}

function fmt(iso: string | null | undefined, tz: string): string {
  return formatDateInTz(iso, tz) || "—";
}

export function HistoricalDefaultsList({
  cases,
  onSelect,
}: HistoricalDefaultsListProps) {
  const { tz } = useDateFormatter();
  if (cases.length === 0) return null;
  return (
    <AccordionCard
      title="Historical default cases"
      testId="historical-defaults"
      badge={<Badge status="archived">{cases.length}</Badge>}
    >
      <ul className="m-0 p-0 list-none">
        {cases.map((c) => (
          <li
            key={c.id}
            className="border-b border-border-light py-space-2 text-small flex items-center justify-between gap-space-3"
          >
            <div>
              <span className="font-mono text-caption">
                {c.id.slice(0, 8).toUpperCase()}
              </span>{" "}
              <Badge status="archived">{c.status}</Badge>
              <p className="m-0 text-caption text-gray-mid">
                Triggered {formatCalendarDate(c.triggerDate) || "—"} → Closed {fmt(c.closedDate, tz)} •{" "}
                {c.resolutionType ?? "no resolution"}
              </p>
            </div>
            {onSelect && (
              <button
                type="button"
                className="text-gold-shadow hover:underline text-small"
                onClick={() => onSelect(c.id)}
              >
                View
              </button>
            )}
          </li>
        ))}
      </ul>
    </AccordionCard>
  );
}
